import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { CheckCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { token } = useAuth();
  const { clearCart } = useCart();

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
        clearCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-artsy-background flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="card-pricing text-center">
          {status === 'checking' || status === 'processing' ? (
            <>
              <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Processing Payment</h1>
              <p className="font-body text-slate-600">Please wait while we confirm your payment...</p>
            </>
          ) : status === 'success' ? (
            <>
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-teal-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
              <p className="font-body text-slate-600 mb-6">
                Thank you for your payment. A confirmation email will be sent shortly.
              </p>
              {paymentDetails && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <p className="font-body text-slate-700">
                    <span className="font-semibold">Amount:</span> £{paymentDetails.amount}
                  </p>
                </div>
              )}
              <Link to="/portal" className="btn-primary inline-flex items-center gap-2" data-testid="goto-portal">
                Go to Portal <ArrowRight className="w-5 h-5" />
              </Link>
            </>
          ) : status === 'timeout' || status === 'error' ? (
            <>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Payment Status Unknown</h1>
              <p className="font-body text-slate-600 mb-6">
                We couldn't confirm your payment status. Please check your email for confirmation or contact us.
              </p>
              <Link to="/portal" className="btn-primary" data-testid="goto-portal-error">
                Go to Portal
              </Link>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Payment Expired</h1>
              <p className="font-body text-slate-600 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/art-club" className="btn-primary" data-testid="try-again">
                Try Again
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
